import { CompactKeyList } from 'components/common';
import { RowLayout } from 'components/core/ListTable/types';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { findLast, last } from 'lodash';
import { TActiveColumn, TActiveColumns } from 'utils/context/ViewContext/types';
import { scopedT } from 'utils/functions/Translations';
import { TPermissions } from 'utils/hooks/usePermissions/usePermissions';

import { IOrder, ShipmentEntity } from '@project44-models/shipment-list';

type TusePermissions = {
  flags: LDFlagSet;
  permissions: TPermissions;
};

const mapRefHeaders = ({ id, label }: TActiveColumn) => ({
  id,
  label,
  width: '220px',
});

const mapRefColumns = ({ id, label }: TActiveColumn) => ({
  id,
  element: ({ customAttributes = [] }: ShipmentEntity) => {
    const attr = customAttributes.find(({ name }) => name === label);
    const keys: string[] = attr?.values || [];
    return <CompactKeyList namespace={label} data={keys} />;
  },
});

export const matchSort = <T extends { id: string }>(
  arr1: T[],
  arr2: TActiveColumns,
  permissions?: TusePermissions,
) => {
  const ids = arr2.map(({ id }) => id);

  if (ids.length) {
    arr1.sort((a, b) => (ids.indexOf(a.id) > ids.indexOf(b.id) ? 1 : -1));
  }

  const result = arr1.filter(({ id }) => {
    if (['shared-with', 'visibility-provided'].includes(id)) {
      return permissions?.flags?.featureExc2119;
    }

    if (
      ['poo-port-name', 'poo-departure-time', 'poo-planned-departure'].includes(
        id,
      )
    ) {
      return permissions?.flags?.featureExc1926;
    }

    if (
      ['pod-port-name', 'pod-est-act-arrival', 'pod-planned-arrival'].includes(
        id,
      )
    ) {
      return permissions?.flags?.featureExc1822;
    }

    if (id == 'related-orders') {
      return permissions?.permissions?.canViewOrders;
    }

    if (['est-act-departure', 'planned-departure'].includes(id)) {
      return permissions?.flags?.featureExc1914;
    }
    return true;
  });
  return result;
};

export const matchEnabledSort = <T extends { id: string }>(
  arr1: T[],
  arr2: TActiveColumns,
  permissions?: TusePermissions,
) => {
  const sort = matchSort(arr1, arr2, permissions);
  return sort.filter(({ id }) => {
    const { enabled } = arr2.find((arr) => id === arr.id) || { enabled: true };
    return enabled;
  });
};

export const matchColumns = (
  // TODO fix any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headers: any,
  rows: RowLayout<ShipmentEntity> | RowLayout<IOrder>,
  activeColumns: TActiveColumns,
  permissions?: TusePermissions,
) => {
  const added = activeColumns.filter(({ id }) => /ref-/i.test(id));
  return [
    [...headers, ...added.map(mapRefHeaders)],
    [...rows, ...added.map(mapRefColumns)],
  ].map((item) =>
    matchSort<typeof item[number]>(item, activeColumns, permissions),
  );
};

export const matchEnabledColumns = (
  // TODO fix any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headers: any,
  rows: RowLayout<ShipmentEntity> | RowLayout<IOrder>,
  activeColumns: TActiveColumns,
  permissions?: TusePermissions,
) => {
  const added = activeColumns.filter(({ id }) => /ref-/i.test(id));
  return [
    [...headers, ...added.map(mapRefHeaders)],
    [...rows, ...added.map(mapRefColumns)],
  ].map((item) =>
    matchEnabledSort<typeof item[number]>(item, activeColumns, permissions),
  );
};

interface IGrouping {
  id: string;
  label: string;
}

const t = scopedT();

export const toggleGroupHeaderOverride: { [key: string]: string } = {
  [t('core.sharedWith')]: t('core.sharedVisibility'),
};

export const toggleGroupConfig: { [key: string]: IGrouping[] } = {
  destination: [
    { id: 'destination', label: t('core.destinationLocationName') },
    { id: 'est-act-arrival', label: t('core.arrivalTime') },
    { id: 'planned-arrival', label: t('core.plannedArrival') },
  ],
  'shared-with': [
    { id: 'shared-with', label: t('core.sharedWith') },
    { id: 'visibility-provided', label: t('core.visibilityProvidedBy') },
  ],
  'poo-port-name': [
    { id: 'poo-port-name', label: t('core.portCode') },
    { id: 'poo-departure-time', label: t('sort.departureTime') },
    { id: 'poo-planned-departure', label: t('sort.plannedDeparture') },
  ],
  'pod-port-name': [
    {
      id: 'pod-port-name',
      label: t('core.portCode'),
    },
    { id: 'pod-est-act-arrival', label: t('sort.dischargeTime') },
    {
      id: 'pod-planned-arrival',
      label: t('sort.plannedDischarge'),
    },
  ],
};

export const toggleGroupConfigExtended: { [key: string]: IGrouping[] } = {
  ...toggleGroupConfig,
  origin: [
    { id: 'origin', label: t('core.originLocationName') },
    { id: 'est-act-departure', label: t('core.departureTime') },
    { id: 'planned-departure', label: t('core.plannedDeparture') },
  ],
};

export const filterGroupOptions = () => {
  let next: string[] = [];
  for (const key in toggleGroupConfig) {
    next = next.concat(
      toggleGroupConfig[key].filter(({ id }) => id !== key).map(({ id }) => id),
    );
  }
  return next;
};

export const filterGroupOptionsExtended = () => {
  let next: string[] = [];
  for (const key in toggleGroupConfigExtended) {
    next = next.concat(
      toggleGroupConfigExtended[key]
        .filter(({ id }) => id !== key)
        .map(({ id }) => id),
    );
  }
  return next;
};

export const getActiveToggleGroupMap = (toggleOptions: string[]) => {
  const op: { [key: string]: string[] } = {};
  for (const key in toggleGroupConfig) {
    const ids = toggleGroupConfig[key].map(({ id }) => id);
    const intersection = ids.filter((id) => toggleOptions.includes(id));
    op[key] = intersection;
  }
  return op;
};

export const getActiveToggleGroupMapExtended = (toggleOptions: string[]) => {
  const op: { [key: string]: string[] } = {};
  for (const key in toggleGroupConfigExtended) {
    const ids = toggleGroupConfigExtended[key].map(({ id }) => id);
    const intersection = ids.filter((id) => toggleOptions.includes(id));
    op[key] = intersection;
  }
  return op;
};

export const getToggleGroupMap = () => {
  const op: { [key: string]: string[] } = {};
  for (const key in toggleGroupConfig) {
    const ids = toggleGroupConfig[key].map(({ id }) => id);
    op[key] = ids;
  }
  return op;
};

export const getToggleGroupMapExtended = () => {
  const op: { [key: string]: string[] } = {};
  for (const key in toggleGroupConfigExtended) {
    const ids = toggleGroupConfigExtended[key].map(({ id }) => id);
    op[key] = ids;
  }
  return op;
};

interface GroupWithColumns {
  isEnabled: boolean;
  columns: string[];
}

interface ColumnWithGroupId extends TActiveColumn {
  groupId: string | undefined;
}

interface ManifestBuildingOptions {
  forceSingleColumns?: string[];
}

export function buildManifest(
  activeColumns: TActiveColumns,
  options: ManifestBuildingOptions = {},
) {
  const { forceSingleColumns = [] } = options;
  const groupIds = Object.keys(toggleGroupConfigExtended);

  const columnIds = activeColumns.map((col) => col.id);

  const columns = activeColumns.reduce(
    (result: Record<string, ColumnWithGroupId>, col) => {
      result[col.id] = Object.assign({}, col, {
        groupId: groupIds.find((groupId) =>
          toggleGroupConfigExtended[groupId].some(({ id }) => id === col.id),
        ),
      });

      return result;
    },
    {},
  );

  const groups = groupIds.reduce(
    (result: Record<string, GroupWithColumns>, groupId) => {
      const groupColumns = toggleGroupConfigExtended[groupId].map(
        (col) => col.id,
      );

      result[groupId] = {
        isEnabled: forceSingleColumns.includes(groupId)
          ? columns[groupId].enabled
          : groupColumns.some((columnId) => {
              return columns[columnId]?.enabled;
            }),
        columns: groupColumns,
      };

      return result;
    },
    {},
  );

  const activeColumnsCopy = Array.from(activeColumns);

  groupIds.forEach((groupId) => {
    const columns = groups[groupId].columns;
    const groupStartIdx = activeColumnsCopy.findIndex(
      (col) => col.id === groupId,
    );

    columns.forEach((columnId, position) => {
      if (columnId !== groupId) {
        const idx = activeColumnsCopy.findIndex((col) => col.id === columnId);
        const grabbedColumn = activeColumnsCopy.splice(idx, 1)[0];
        activeColumnsCopy.splice(groupStartIdx + position, 0, grabbedColumn);
      }
    });
  });

  const getGroupId = (columnId: string) => {
    return columns[columnId].groupId;
  };

  const getFirstDisabledColumn = () => {
    return (
      columnIds.find((columnId) => {
        const groupId = getGroupId(columnId);
        if (groupId) {
          return !groups[groupId].isEnabled;
        } else {
          return !columns[columnId].enabled;
        }
      }) || last(columnIds)
    );
  };

  const getLastEnabledColumn = () => {
    return (
      findLast(columnIds, (columnId) => {
        const groupId = getGroupId(columnId);
        if (groupId) {
          return groups[groupId].isEnabled;
        } else {
          return columns[columnId].enabled;
        }
      }) || columns[0]
    );
  };

  return {
    groupIds,
    groups,
    columnIds,
    columns,
    getFirstDisabledColumn,
    getLastEnabledColumn,
    getGroupId,
    activeColumnsCopy,
  };
}
